import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

/* ===========================================================================
   Primitivas de interfaz.
   Convención: componentes y props en inglés; todo texto visible, en español.
   =========================================================================== */

function cx(...partes: Array<string | false | null | undefined>): string {
  return partes.filter(Boolean).join(' ')
}

// --- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-tinta text-bruma hover:bg-tinta-suave',
  secondary: 'bg-transparent text-tinta border border-palo hover:bg-palo/30',
  ghost: 'bg-transparent text-palo-hondo hover:text-tinta hover:bg-palo/25',
  danger: 'bg-ladrillo text-lienzo hover:opacity-90',
}

type ButtonSize = 'md' | 'sm'

/* La talla md reserva 44x44 px de área táctil (páginas públicas, formularios).
   La sm es para barras de herramientas y filas del panel de escritorio, donde
   44 px por botón inflan cada fila; aun así no baja de 36 px. */
const BUTTON_SIZES: Record<ButtonSize, string> = {
  md: 'px-4 py-2.5 min-h-11 min-w-11',
  sm: 'px-3 py-1.5 min-h-9 min-w-9',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Muestra un spinner y bloquea el botón. */
  loading?: boolean
  full?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  full = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium',
        BUTTON_SIZES[size],
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_STYLES[variant],
        full && 'w-full',
        className,
      )}
    >
      {loading && <Spinner small />}
      {children}
    </button>
  )
}

// --- Campos -----------------------------------------------------------------

/* El borde es el único límite visible del campo sobre la página: va en
   palo-hondo (3:1 largo contra bruma y contra el propio relleno lienzo), el
   placeholder a opacidad completa, y sin outline-none para que el anillo de
   foco de :focus-visible se dibuje también aquí. */
const CONTROL =
  'w-full rounded-md border border-palo-hondo bg-lienzo px-3 py-2 text-sm text-tinta ' +
  'placeholder:text-palo-hondo focus:border-laton ' +
  'disabled:opacity-60'

interface CampoBase {
  label: string
  hint?: string
  error?: string
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement>, CampoBase {}

export function Field({ label, hint, error, className, id, ...rest }: FieldProps) {
  const auto = useId()
  const fieldId = id ?? auto
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-tinta">
        {label}
      </label>
      <input
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cx(CONTROL, error && 'border-ladrillo', className)}
      />
      {error ? (
        <p id={`${fieldId}-error`} className="text-xs text-ladrillo">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-xs text-palo-hondo">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, CampoBase {}

export function TextArea({ label, hint, error, className, id, ...rest }: TextAreaProps) {
  const auto = useId()
  const fieldId = id ?? auto

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-tinta">
        {label}
      </label>
      <textarea
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cx(CONTROL, 'min-h-24 resize-y', error && 'border-ladrillo', className)}
      />
      {error ? (
        <p className="text-xs text-ladrillo">{error}</p>
      ) : hint ? (
        <p className="text-xs text-palo-hondo">{hint}</p>
      ) : null}
    </div>
  )
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, CampoBase {
  options: Array<{ value: string; label: string }>
}

export function Select({ label, hint, error, options, className, id, ...rest }: SelectProps) {
  const auto = useId()
  const fieldId = id ?? auto

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-tinta">
        {label}
      </label>
      <select {...rest} id={fieldId} className={cx(CONTROL, className)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? (
        <p className="text-xs text-ladrillo">{error}</p>
      ) : hint ? (
        <p className="text-xs text-palo-hondo">{hint}</p>
      ) : null}
    </div>
  )
}

export function Switch({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  hint?: string
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[var(--color-verdemar)]"
      />
      <span>
        <span className="block text-sm text-tinta">{label}</span>
        {hint && <span className="block text-xs text-palo-hondo">{hint}</span>}
      </span>
    </label>
  )
}

// --- Superficies ------------------------------------------------------------

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cx('rounded-lg border border-palo/70 bg-lienzo p-4', className)}>{children}</div>
  )
}

/**
 * Cabecera de sección o de pestaña del panel. Las acciones van a la derecha del
 * título —no en una fila propia debajo—, que es lo que mantiene compacta cada
 * pantalla; la descripción es una línea de contexto, no un párrafo.
 */
export function SectionTitle({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
        <h2 className="font-display text-2xl text-tinta">{title}</h2>
        {description && <p className="mt-1 max-w-prose text-sm text-palo-hondo">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

// --- Cifras -----------------------------------------------------------------

export interface StatItem {
  label: string
  value: ReactNode
  hint?: string
}

/**
 * Tira de cifras: los números clave de una pantalla agrupados en un solo marco,
 * separados por filetes finos. Sustituye a los contadores sueltos flotando en
 * el vacío; es el mismo lenguaje en Resumen, Invitados y Presupuesto.
 */
export function StatStrip({ items, className }: { items: StatItem[]; className?: string }) {
  return (
    <dl
      className={cx(
        'grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-palo/70 bg-palo/60',
        'sm:grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))]',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="bg-lienzo px-4 py-3">
          <dt className="eyebrow">{item.label}</dt>
          <dd className="tabular mt-1.5 font-mono text-2xl leading-none text-tinta">
            {item.value}
          </dd>
          {item.hint && <dd className="mt-1 text-xs text-palo-hondo">{item.hint}</dd>}
        </div>
      ))}
    </dl>
  )
}

type BadgeTone = 'neutral' | 'positivo' | 'aviso' | 'negativo' | 'laton'

const BADGE_STYLES: Record<BadgeTone, string> = {
  neutral: 'bg-palo/45 text-tinta',
  positivo: 'bg-verdemar/15 text-verdemar',
  // Latón sobre latón no se lee: el texto va en tinta. 10.2:1 sobre bruma.
  aviso: 'bg-laton/25 text-tinta',
  negativo: 'bg-ladrillo/12 text-ladrillo',
  laton: 'bg-laton text-lienzo',
}

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        BADGE_STYLES[tone],
      )}
    >
      {children}
    </span>
  )
}

// --- Estados ----------------------------------------------------------------

export function Spinner({ small = false }: { small?: boolean }) {
  return (
    <span
      role="status"
      aria-label="Cargando"
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        small ? 'size-3.5' : 'size-5',
      )}
    />
  )
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-palo-hondo">
      <Spinner />
      <span className="text-sm">{label}</span>
    </div>
  )
}

/** Una pantalla vacía es una invitación a actuar, no un mensaje de error. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed border-palo bg-lienzo/60 px-6 py-8 text-center">
      <p className="font-display text-lg text-tinta">{title}</p>
      {description && <p className="mx-auto mt-1.5 max-w-md text-sm text-palo-hondo">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

/** Los errores explican qué pasó y cómo salir, sin disculparse. */
export function ErrorMessage({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-ladrillo/30 bg-ladrillo/8 px-3 py-2 text-sm text-ladrillo"
    >
      {children}
    </p>
  )
}

// --- Modal ------------------------------------------------------------------

const ENFOCABLES =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

type ModalSize = 'md' | 'lg'

/* Un solo ancho embutía cualquier formulario a dos columnas en 512px:
   lg da sitio a los diálogos con rejillas internas sin tocar los demás. */
const MODAL_SIZES: Record<ModalSize, string> = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  size?: ModalSize
}) {
  const panel = useRef<HTMLDivElement>(null)

  // El efecto de abajo depende solo de `open`: si dependiera de onClose,
  // cada render del contenedor le robaría el foco a quien esté escribiendo.
  const cerrar = useRef(onClose)
  useEffect(() => {
    cerrar.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return

    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null

    const listaEnfocables = (): HTMLElement[] => {
      const raiz = panel.current
      if (!raiz) return []
      return Array.from(raiz.querySelectorAll<HTMLElement>(ENFOCABLES)).filter(
        (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
      )
    }

    const alPresionar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cerrar.current()
        return
      }
      if (e.key !== 'Tab') return

      const raiz = panel.current
      if (!raiz) return
      const lista = listaEnfocables()
      const primero = lista[0]
      const ultimo = lista[lista.length - 1]
      const activo = document.activeElement

      // Sin nada que enfocar dentro, el foco se queda en el propio panel.
      if (!primero || !ultimo) {
        e.preventDefault()
        raiz.focus()
        return
      }

      const dentro = activo instanceof Node && raiz.contains(activo)
      if (e.shiftKey) {
        if (!dentro || activo === primero || activo === raiz) {
          e.preventDefault()
          ultimo.focus()
        }
      } else if (!dentro || activo === ultimo) {
        e.preventDefault()
        primero.focus()
      }
    }

    document.addEventListener('keydown', alPresionar)
    panel.current?.focus()

    // La página de fondo no se desplaza mientras el diálogo está abierto.
    const desbordePrevio = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', alPresionar)
      document.body.style.overflow = desbordePrevio
      previo?.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tinta/45 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          'max-h-[90vh] w-full overflow-y-auto overscroll-contain rounded-t-xl bg-lienzo p-5 sm:rounded-xl',
          MODAL_SIZES[size],
        )}
      >
        <h2 className="font-display text-xl text-tinta">{title}</h2>
        <div className="mt-4">{children}</div>
        {footer && <div className="mt-5 flex justify-end gap-2 border-t border-palo/60 pt-4">{footer}</div>}
      </div>
    </div>
  )
}

// --- Avisos (toasts) --------------------------------------------------------

interface Aviso {
  id: number
  texto: string
  tono: 'ok' | 'error'
}

interface ToastValue {
  /** Confirma en pasado lo que la acción prometía: "Publicar" -> "Publicado". */
  ok(texto: string): void
  error(texto: string): void
}

const ToastContext = createContext<ToastValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])
  const contador = useRef(0)

  const empujar = useCallback((texto: string, tono: Aviso['tono']) => {
    const id = ++contador.current
    setAvisos((previos) => [...previos, { id, texto, tono }])
    setTimeout(() => setAvisos((previos) => previos.filter((a) => a.id !== id)), 4500)
  }, [])

  const valor = useMemo<ToastValue>(
    () => ({
      ok: (texto) => empujar(texto, 'ok'),
      error: (texto) => empujar(texto, 'error'),
    }),
    [empujar],
  )

  return (
    <ToastContext.Provider value={valor}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {avisos.map((aviso) => (
          <div
            key={aviso.id}
            className={cx(
              'pointer-events-auto rounded-md px-4 py-2.5 text-sm shadow-lg',
              aviso.tono === 'ok' ? 'bg-tinta text-bruma' : 'bg-ladrillo text-lienzo',
            )}
          >
            {aviso.texto}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastValue {
  const valor = useContext(ToastContext)
  if (!valor) throw new Error('useToast() fuera de <ToastProvider>')
  return valor
}
